import { useApolloClient } from "@apollo/client/react";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPagePolicy } from "hooks/useAnalytics/pages/policy";
import useCanManagePolicy from "shared/Policy/access/useCanManagePolicy";
import { Policy } from "types/generated";
import { showPolicyEditDrawer } from "views/Account/PolicyEditDrawer";
import { showDeleteConfirmation } from "views/Account/Policies/DeleteConfirmation";
import { GET_POLICY_NAMES } from "views/Account/Policies/useTakenPolicyNames";

import { REFETCH_QUERIES_ON_DELETE } from "../../constants";

const PoliciesTableDropdownCell = ({
  item: policy,
  ...cellProps
}: TableSimpleCellProps<Policy>) => {
  const client = useApolloClient();
  const trackAnalytics = useAnalytics({ page: AnalyticsPagePolicy.PoliciesList });

  const canManagePolicy = useCanManagePolicy()({
    space: policy.spaceDetails.id,
    spaceDetails: policy.spaceDetails,
    plugin: policy.plugin,
  });
  const showActionButtons = canManagePolicy.result || !!canManagePolicy.message;

  const handleEdit = () => {
    const names = client.readQuery<{ policies: Array<{ name: string }> }>({
      query: GET_POLICY_NAMES,
    });

    showPolicyEditDrawer({
      id: policy.id,
      analyticsPage: AnalyticsPagePolicy.PoliciesList,
      takenPolicyNames: names?.policies.map((p) => p.name.toLowerCase()) ?? [],
      policy,
      refetchQueries: REFETCH_QUERIES_ON_DELETE,
    });
  };

  const handleDelete = () => {
    showDeleteConfirmation({
      id: policy.id,
      hasAttachedEntities: policy.hasAttachedStacks,
      name: policy.name,
    }).then((finished) => {
      if (finished) trackAnalytics("Confirm Delete");
    });
  };

  return (
    <TableSimpleCell {...cellProps}>
      <DropdownMenuEllipsis tooltip="Policy actions">
        {showActionButtons && (
          <DropdownMenuItem
            tooltip={canManagePolicy.message}
            isDisabled={!canManagePolicy.result}
            onAction={handleEdit}
          >
            Edit details
          </DropdownMenuItem>
        )}
        <CopyFieldDropdownMenuItem title="Copy policy ID" value={policy.id} />
        {showActionButtons && (
          <DropdownMenuItem
            tooltip={canManagePolicy.message}
            isDisabled={!canManagePolicy.result}
            onAction={handleDelete}
            danger
          >
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuEllipsis>
    </TableSimpleCell>
  );
};

export default PoliciesTableDropdownCell;
